import {
  EffectParams,
  SchemaBoolean,
  SchemaNumber,
  SchemaNumberWithUnit,
  SchemaString,
} from '@wix/effects-kit';
import { Divider } from '@wix/wix-base-ui';
import React from 'react';
import {
  AnimationCustomizeControlsProps,
  CustomizeControlProps,
} from '../types';
import {
  DropDownList,
  SliderNumeric,
  SliderNumericWithUnit,
  BooleanSwitch,
  DirectionDropDownList,
  AngleSlider,
  ShapeThumbnails,
} from './AnimationCustomizeComponents';

const getControls = ({
  effectName,
  schema,
  values,
  onChange = () => {},
}: AnimationCustomizeControlsProps) => {
  const onControlChange = (name: string, value: any) => {
    onChange(effectName, name, value);
  };

  const hasStartFromOffScreen =
    Object.keys(schema).includes('startFromOffScreen');

  return (
    Object.entries(schema)
      // We don't currently use allowReplay
      // We filter out all hidden controls
      .filter(([name, params]) => name !== 'allowReplay' && !params.hidden)
      // Now we sort the controls
      .sort(([a], [b]) => {
        // In Entrance and Loop Duration and Delay should always be at the end
        if (b === 'delay' || (a !== 'delay' && b === 'duration')) {
          return -1;
        }

        // In Scroll Start and End should always be at the end
        if (b === 'end' || (a !== 'end' && b === 'start')) {
          return -1;
        }

        // Power should always be at the beginning
        if (a === 'power') {
          return -1;
        }

        // startFromOffScreen should always be one after distance
        if (a === 'distance' && b === 'startFromOffScreen') {
          return -1;
        }
        return 0;
      })
      .map(([name, params], index, arr) => {
        // should be disabled?
        const disabled =
          hasStartFromOffScreen &&
          (values as any).startFromOffScreen &&
          name === 'distance';
        // should show a divider?
        const divider =
          !(hasStartFromOffScreen && name === 'distance') &&
          index < arr.length - 1;

        const value = values[name as keyof EffectParams] as any;

        const controlProps = {
          name: name as keyof EffectParams,
          params,
          divider,
          value,
          onChange: onControlChange,
          key: `control${index}`,
          disabled,
        };

        switch (params.type) {
          case 'number':
            if (params.enum) {
              if (name === 'angle') {
                return (
                  <DirectionDropDownList
                    {...(controlProps as CustomizeControlProps<SchemaString>)}
                  />
                );
              }
              return (
                <DropDownList
                  {...(controlProps as CustomizeControlProps<SchemaString>)}
                />
              );
            }

            if (params.min === 0 && params.max === 360) {
              return (
                <AngleSlider
                  {...(controlProps as CustomizeControlProps<SchemaNumber>)}
                />
              );
            }

            return (
              <SliderNumeric
                {...(controlProps as CustomizeControlProps<SchemaNumber>)}
              />
            );
          case 'number-with-unit':
            return (
              <SliderNumericWithUnit
                {...(controlProps as CustomizeControlProps<SchemaNumberWithUnit>)}
              />
            );
          case 'string':
            if (name === 'direction' || name === 'spin') {
              return (
                <DirectionDropDownList
                  {...(controlProps as CustomizeControlProps<SchemaString>)}
                />
              );
            }
            if (name === 'shape') {
              return (
                <ShapeThumbnails
                  {...(controlProps as CustomizeControlProps<SchemaString>)}
                />
              );
            }
            return (
              <DropDownList
                {...(controlProps as CustomizeControlProps<SchemaString>)}
              />
            );
          case 'boolean':
            return (
              <BooleanSwitch
                {...(controlProps as CustomizeControlProps<SchemaBoolean>)}
              />
            );
          default:
            return (
              <div key={`control${index}`}>
                <span>Unknown control type for {name}</span>
              </div>
            );
        }
      })
  );
};

export const AnimationCustomizeControls: React.FC<
  AnimationCustomizeControlsProps
> = (props) => {
  const controls = getControls(props);
  // Some very few animations have no controls, we need to not show the divider in this case
  if (controls.length && props.divider) {
    controls.unshift(<Divider long={false} />);
  }
  return <>{controls}</>;
};
